import { copyOf } from '../constants/copy-deck.prod-fixes';

/**
 * SMALL-4 (prod fixes 2026-10-02, UX-SPEC section 12): what a failed SVG compress or PDF to EPUB card says.
 *
 * The edge (ironman `CompressVector` / `EbookConvert`) classifies the failure and sends a closed-set
 * `processResult.failureDetailCode`; the card shows the matching copy-deck line instead of the terminal
 * frame's bare "Processing failed". Keyed on the code only, never on server text: the tool's own output stays
 * in `errorLogs[1..]` for diagnosis and is never rendered. A result without one of these codes renders exactly
 * as before, so this needs no flag of its own (an older edge simply never sends them).
 *
 * A leaf on purpose (copy deck and constants only).
 */

export const SVG_FAILED = 'SVG_FAILED';
export const EBOOK_TIMEOUT = 'EBOOK_TIMEOUT';
export const EBOOK_FAILED = 'EBOOK_FAILED';
export type ToolFailureCode = typeof SVG_FAILED | typeof EBOOK_TIMEOUT | typeof EBOOK_FAILED;

/** X12-2's action: the existing Split PDF tool (UX-SPEC section 12), same tab, its own page name as the label. */
export const SPLIT_PDF_LINK = { text: 'Split PDF', href: '/split-pdf' } as const;

/**
 * X12-1's action (UX4-03, fu-t17): the copy says "convert it to PNG instead", so the card links the existing SVG to PNG
 * tool, same tab, its page name as the label.
 */
export const SVG_TO_PNG_LINK = { text: 'SVG to PNG', href: '/convert-svg-to-png' } as const;

export type ToolFailureAction = typeof SPLIT_PDF_LINK | typeof SVG_TO_PNG_LINK;

const COPY_ID = { [SVG_FAILED]: 'X12-1', [EBOOK_TIMEOUT]: 'X12-2', [EBOOK_FAILED]: 'X12-3' } as const;

export function isToolFailureCode(code: unknown): code is ToolFailureCode {
    return code === SVG_FAILED || code === EBOOK_TIMEOUT || code === EBOOK_FAILED;
}

/** The code on a card's result (`fileUpload.result`), or null when there is none or it is not one of ours. */
export function toolFailureOf(result: unknown): ToolFailureCode | null {
    const r = result as { error?: unknown; failureDetailCode?: unknown } | null | undefined;
    if (!r || r.error !== true || !isToolFailureCode(r.failureDetailCode)) return null;
    return r.failureDetailCode;
}

/** X12-1 / X12-2 / X12-3. */
export function toolFailureMessage(code: ToolFailureCode): string {
    return copyOf(COPY_ID[code]);
}

/**
 * The one action a tool failure offers -- the remedy its copy names: Split PDF for a PDF to EPUB that hit the time
 * limit, SVG to PNG for an SVG svgo could not process; nothing for X12-3. It is the card's PRIMARY key (UX4-03): running
 * the same file again hits the same limit, so Re-edit steps back to secondary ("never tell the user to retry something
 * we know will fail again").
 */
export function toolFailureAction(code: ToolFailureCode): ToolFailureAction | null {
    if (code === EBOOK_TIMEOUT) return SPLIT_PDF_LINK;
    if (code === SVG_FAILED) return SVG_TO_PNG_LINK;
    return null;
}

/** The action of a card's result, or null (no tool-failure code, or one without an action). */
export function toolFailureActionOf(result: unknown): ToolFailureAction | null {
    const code = toolFailureOf(result);
    return code ? toolFailureAction(code) : null;
}
